import { apiFetch } from './client';
import type { Page } from './discover';

/**
 * Likes and comments (Phase 18) — what readers say about a story, in public.
 *
 * The public half of a pair the product already had: a favourite is private
 * and nothing counts it, a like is counted where everyone can see it. Neither
 * can be about your own story, and the backend is what decides that — `can_like`
 * on the story says whether to offer the button at all (Rule 7).
 */

export interface Comment {
  id: string;
  author: { handle: string; display_name: string };
  body: string;
  is_mine: boolean;
  /** Taken out of view by a moderator. Only its own author is ever shown one. */
  is_hidden: boolean;
  /** Why, for its author. Empty for everybody else. */
  hidden_reason: string;
  created_at: string;
  edited_at: string | null;
}

export interface LikeState {
  id: string;
  is_liked: boolean;
  like_count: number;
}

function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export const likePost = (postId: string): Promise<LikeState> =>
  send<LikeState>(`/posts/${postId}/like`, 'POST');

export const unlikePost = (postId: string): Promise<LikeState> =>
  apiFetch<LikeState>(`/posts/${postId}/like`, { method: 'DELETE' });

export const listComments = (postId: string, page = 1): Promise<Page<Comment>> =>
  apiFetch<Page<Comment>>(`/posts/${postId}/comments${page > 1 ? `?page=${page}` : ''}`);

export const addComment = (postId: string, body: string): Promise<Comment> =>
  send<Comment>(`/posts/${postId}/comments`, 'POST', { body });

export const editComment = (postId: string, commentId: string, body: string): Promise<Comment> =>
  send<Comment>(`/posts/${postId}/comments/${commentId}`, 'PATCH', { body });

export const deleteComment = (postId: string, commentId: string): Promise<void> =>
  apiFetch<void>(`/posts/${postId}/comments/${commentId}`, { method: 'DELETE' });

export const reportComment = (postId: string, commentId: string, kind: string, details: string) =>
  send<{ id: string; status: string; detail: string }>(
    `/posts/${postId}/comments/${commentId}/report`,
    'POST',
    { kind, details },
  );

/** Moderator only: out of view, with a reason its author reads. Never deleted. */
export const hideComment = (commentId: string, reason: string) =>
  send<{ id: string; is_hidden: boolean }>(`/moderation/comments/${commentId}/hidden`, 'PUT', {
    reason,
  });

export const unhideComment = (commentId: string) =>
  apiFetch<{ id: string; is_hidden: boolean }>(`/moderation/comments/${commentId}/hidden`, {
    method: 'DELETE',
  });
